import { clsx } from "clsx";
import type { FC, MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, type NavLinkProps } from "react-router-dom";
import { titleCase } from "title-case";

import { Tag, TagIntent } from "widgets";
import { useResizeObserver } from "widgets/hooks";

import styleSkeleton from "../../core/styles/loading-skeleton.module.scss";
import style from "./styles/NavigationCategory.module.scss";

import { TooltipController } from "../overlays/components/OverlayControllers.js";
import type { ILicensable } from "@/redux/modules/config.js";

export interface IRouteNavItemProps extends ILicensable {
  // UI
  label: string;
  badge?: string;
  disabled?: boolean;
  isLoading?: boolean;

  // router
  route: string; // for building the link
  matchingRoute?: string; // for building the route with params (by default the route is used) (router params will be available via useParams() hook)
  exact?: boolean; // for building the route, since the /* is added to the matching route, except if the route should be exactly the provided one
  // TODO: Check that
  routeKey?: string; // allows to keep a component from unmounting/mounting again between route changes
  search?: string; // for providing query parameters
  pageComponent: ReactNode;

  hotspot?: string; // corresponds to the selector id of the tip
  disabledTooltip?: string; // optional tooltip

  default?: boolean; // allows the redirects ; if more than one the first is selected
  eventKey?: string; // sort of a payload (also in INavPanelCategory) // TODO: Bootstrap only?
}

/**
 * Wrapper component of NavItem that allows to pass a relative path and makes sure the menu item is
 * provided the "active" class when required
 * Should be used within a Nav component.
 */
export const RouteNavItem: FC<IRouteNavItemProps> = (props) => {
  /**
   * Disable the location change when clicking on the item and the link is actually disabled
   */
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (props.disabled) {
        event.preventDefault();
      }
    },
    [props.disabled],
  );

  // Router;
  const navLinkProps: NavLinkProps = {
    to: encodeURI(props.route), // encode because of the % char
    //className: ({ isActive }) => (isActive ? style.active : ""),
    end: props.exact === true,
    onClick: handleClick,
  };

  // Tooltip
  const [showCompleteLabel, setShowCompleteLabel] = useState(false);
  const tooltipMessage = useMemo(
    () =>
      props.disabled && props.disabledTooltip ? props.disabledTooltip : showCompleteLabel ? props.label : undefined,
    [props.disabled, props.disabledTooltip, props.label, showCompleteLabel],
  );
  const labelRef = useRef<HTMLSpanElement>(null);
  const labelRect = useResizeObserver(labelRef);

  useEffect(() => {
    setShowCompleteLabel(!!labelRect?.widthOverflow);
  }, [labelRect]);

  return (
    <>
      <TooltipController
        content={tooltipMessage}
        overlayOptions={{
          placement: props.disabled ? "top" : "right",
          isDisabled: !tooltipMessage || props.isLoading,
        }}
      >
        {(tooltipTriggerProps) => (
          <NavLink
            key={"nav-" + props.route}
            {...tooltipTriggerProps}
            {...navLinkProps}
            className={({ isActive }) =>
              clsx(style.item, {
                [style.item_disabled]: props.disabled || props.isLoading,
                [style.item_active]: isActive && !props.isLoading,
              })
            }
          >
            {props.isLoading ? (
              <div className={clsx(style.item_link, style.item_link_disabled)}>
                <span className={clsx(style.label, styleSkeleton.skeleton)}>{props.label}</span>
              </div>
            ) : (
              <div className={clsx(style.item_link, { [style.item_link_disabled]: props.disabled })}>
                {props.hotspot && <span id={props.hotspot} className="hotspot-beacon" />}
                <span className={style.label} ref={labelRef}>
                  {titleCase(props.label)}
                </span>
                {props.badge != undefined && (
                  <Tag label={props.badge} intentColor={TagIntent.Outlined} style={{ marginLeft: 5 }} />
                )}
              </div>
            )}
          </NavLink>
        )}
      </TooltipController>
    </>
  );
};
